import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import momentTz from 'moment-timezone';

import { getResolutionName, getCustomerLastName } from "../../containers/WorkkardDetail/utils";
import { completeDiary, cancelDiary, resetViewDiaryInfo, changeViewActiveTab } from "../../containers/Diaries/actions";
import { changeDiarieActionCancelled } from '../../containers/WorkkardDetail/actions/action';
import { showDialogueBox, hideDialogueBox } from '../DialogueBox/actions';
import Icon              from "../../icons";
import Detail            from "./detail";
import Logs              from "./logs";
import DiaryCreation     from "../../containers/CreationForms/DiaryCreation";
import ConfirmationTextBox from "../ConfirmationPrompt";
import * as CommonUtil   from '../../common/util';
import { DiaryActions, StringConstants } from "../../containers/WorkkardDetail/constants";
import { DEFAULT_TIME_FORMAT } from '../../common/constants';

// Pure helpers
const resolveReferrer = (dataKey) => {
    if (dataKey === "detail") return "getclmdetail";
    if (dataKey === "views")  return "views";
    return "tdo";
};

const MODAL_FULL = {
    overlayClassName:        "modal fullViewMain",
    contentClassName:        "modalContent fullView animated faster fadeInUpx2 medium",
    shouldCloseOnOverlayClick: false,
};

// Sub-Components
const DiaryStatusBadge = ({ status }) => {
    if (!status) return null;
    const key = status.toLowerCase();
    return (
        <span
            className={`status mr workkardStatus selfStart ${getResolutionName(key, true)}`}
            data-test="diaryStatus"
        >
            {getResolutionName(key)}
        </span>
    );
};

const TabNav = ({ activeTab, onTabClick }) => (
    <div className="tabNav diaryNav">
        <div className="tabs">
            <a href="#" onClick={() => onTabClick(0)} data-test="details"
                className={activeTab === 0 ? "noteIcon active" : "noteIcon"}>
                <Icon name="note" className="icon" /> <span>Details</span>
            </a>
            <a href="#" onClick={() => onTabClick(1)} data-test="logs"
                className={activeTab === 1 ? "attachIcon active" : "attachIcon"}>
                <Icon name="logs" className="icon" /> <span>Logs</span>
            </a>
        </div>
    </div>
);

const DiaryInfo = ({ selectedDiary, dataKey, actId, wkId, onClose }) => {
    const dispatch = useDispatch();

    // Selectors
    const { listingData }                           = useSelector(s => s.diaries.listing);
    const { diaryInfo, workkard, activity, userTimeZone, isAuditUser } = useSelector(s => ({
        diaryInfo:    s.diaries.view.diaryInfo,
        workkard:     s.workkardDetail.workkardDetail,
        activity:     s.workkardDetail.activityDetail,
        userTimeZone: s.sessionContext?.TimeZoneTz,
        isAuditUser:  s.sessionContext?.IsAuditUser,
    }), shallowEqual);

    const data = listingData?.data?.find(d => d.todo_id === selectedDiary) ?? null;

    // Derived
    const workkardNumber = Object.keys(workkard).length ? workkard.WorkkardNumber : activity.WorkkardNo;
    const CustomFields   = Object.keys(workkard).length ? workkard.CustomFields   : activity.WorkkardCustomFields;
    const referrer       = resolveReferrer(dataKey);

    const primaryAction = useMemo(() =>
        CommonUtil.getMenuActions(DiaryActions, data?.menuActions)?.[0]?.value,
    [data?.menuActions]);

    const menuActions = useMemo(() =>
        CommonUtil.getMenuActions(DiaryActions, data?.menuActions)?.slice(1) ?? [],
    [data?.menuActions]);

    // Local state
    const [activeTab,      setActiveTab]      = useState(0);
    const [showMenuAction, setShowMenuAction] = useState(false);
    const menuRef = useRef(null);

    //Outside click
    const handleOutsideClick = useCallback((e) => {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
            setShowMenuAction(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [handleOutsideClick]);

    const onTabClick = useCallback((tab) => {
        setActiveTab(tab);
        dispatch(changeViewActiveTab({ activeTab: tab }));
    }, [dispatch]);

    // Dialogue helpers
    const closeAndReset = useCallback(() => {
        dispatch(hideDialogueBox());
        dispatch(resetViewDiaryInfo());
    }, [dispatch]);

    const openFullModal = useCallback((component) => {
        dispatch(showDialogueBox({ flag: true, component, ...MODAL_FULL }));
    }, [dispatch]);

    // Action handlers
    const handleEditDiary = useCallback(() => {
        setShowMenuAction(false);
        openFullModal(
            <DiaryCreation
                type={StringConstants.common}
                onClose={closeAndReset}
                referrer={referrer}
                isEdit
                todoId={data.todo_id}
            />
        );
    }, [openFullModal, closeAndReset, referrer, data?.todo_id]);

    const handleCompleteDiary = useCallback(() => {
        setShowMenuAction(false);
        dispatch(completeDiary({
            id:            data.todo_id,
            isComplete:    true,
            key:           dataKey,
            completedDate: momentTz().tz(userTimeZone).format(DEFAULT_TIME_FORMAT),
        }));
    }, [dispatch, data?.todo_id, dataKey, userTimeZone]);

    const handleCompleteAndAdd = useCallback(() => {
        setShowMenuAction(false);
        dispatch(completeDiary({
            id:            data.todo_id,
            isComplete:    true,
            key:           dataKey,
            completedDate: momentTz.utc().format('l [at] h:mm A'),
        }));
        openFullModal(
            <DiaryCreation
                actId={actId || (data.module === "activity" ? data.module_id : 0)}
                wkId={wkId   || (data.module === "workkard" ? data.module_id : 0)}
                wkNumber={workkardNumber || (data.module === "workkard" ? data.module_url : 0)}
                type={data.module}
                customerLastName={CustomFields ? getCustomerLastName(CustomFields) : ''}
                onClose={closeAndReset}
                referrer={referrer}
            />
        );
    }, [dispatch, openFullModal, closeAndReset, data, actId, wkId, workkardNumber, CustomFields, referrer, dataKey, userTimeZone]);

    const handleCancelDiary = useCallback(() => {
        setShowMenuAction(false);
        dispatch(showDialogueBox({
            flag: true,
            component: (
                <ConfirmationTextBox
                    className="modalBody"
                    message="Are you sure you wish to cancel this Diary?"
                    onCancelClick={closeAndReset}
                    onConfirmClick={() => {
                        if (dataKey === "detail") {
                            dispatch(changeDiarieActionCancelled({ id: data.todo_id }));
                        } else {
                            dispatch(cancelDiary({ id: data.todo_id, key: dataKey }));
                        }
                        onClose();
                    }}
                    confirmationText="Confirm"
                    footerClass="modalFooter"
                    cancelText="Cancel"
                />
            ),
            overlayClassName:        "modal",
            contentClassName:        "modalContent small animated faster fadeInUpx2 confirmModal",
            shouldCloseOnOverlayClick: true,
        }));
    }, [dispatch, closeAndReset, data?.todo_id, dataKey, onClose]);

    const handleReopenDiary = useCallback(() => {
        setShowMenuAction(false);
        dispatch(completeDiary({
            id:            data.todo_id,
            isComplete:    false,
            key:           dataKey,
            completedDate: momentTz.utc().format('l [at] h:mm A'),
        }));
    }, [dispatch, data?.todo_id, dataKey]);

    // Action map
    const ACTION_MAP = useMemo(() => ({
        [StringConstants.EditDiary]:           handleEditDiary,
        [StringConstants.CompleteDiary]:       handleCompleteDiary,
        [StringConstants.CompleteAndAddDiary]: handleCompleteAndAdd,
        [StringConstants.CancelDiary]:         handleCancelDiary,
        [StringConstants.ReopenDiary]:         handleReopenDiary,
    }), [handleEditDiary, handleCompleteDiary, handleCompleteAndAdd, handleCancelDiary, handleReopenDiary]);

    const onClickDiaryActions = useCallback((e) => {
        ACTION_MAP[e.target.innerText.trim()]?.();
    }, [ACTION_MAP]);

    // Render guards
    if (!data || !Object.keys(data).length) return null;

    const isCancelled  = data.status === "CANCELLED";
    const dropDownClass = `dropDownMenu openRight${showMenuAction ? " rightAlign onClick" : ""}`;

    return (
        <div className="diaresStatus ccCalendar">
            <div className="modalHeader modalDiary">
                <div className="filterBlock rowFlex alignMiddle">
                    <div className="filterLeft">
                        <h2 className="mr__1 completeTitle" data-test="diaryTitle">{data.title}</h2>
                        <DiaryStatusBadge status={data.status} />
                    </div>

                    <div className="filterRight selfStart">
                        {!isCancelled && !isAuditUser && (
                            <div
                                className={`dropDown ${showMenuAction ? "active" : ""}`}
                                ref={menuRef}
                                data-test="menuActions"
                            >
                                <a className="btn pointer buttonGradient newViewsBtn completeBtn">
                                    <span onClick={onClickDiaryActions}>{primaryAction}</span>
                                    <span
                                        className={`actionBtn alignMiddle ${showMenuAction ? "active" : ""}`}
                                        onClick={() => setShowMenuAction(p => !p)}
                                    >
                                        <Icon name="chev" className="icon" />
                                    </span>
                                </a>
                                <div className={dropDownClass}>
                                    {menuActions.map((item, key) => (
                                        <span key={key} onClick={onClickDiaryActions}>
                                            <a href="#">{item.value}</a>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="alignRight crossClose ml">
                            <a href="#" onClick={onClose}>
                                <Icon name="close" className="icon" />
                            </a>
                        </div>
                    </div>
                </div>

                <TabNav activeTab={activeTab} onTabClick={onTabClick} />
            </div>

            <div className="modalBody detailsData">
                {diaryInfo.activeTab === 0 ? (
                    <Detail
                        data={data}
                        status={data.status}
                        dataKey={dataKey}
                        userTimeZone={userTimeZone}
                        galleryData={diaryInfo.galleryData}
                    />
                ) : (
                    <Logs id={data.todo_id} />
                )}
            </div>
        </div>
    );
};

export default DiaryInfo;